import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../hooks/useAPI';
import APP_CONFIG from '../config/appConfig';

const Home = () => {
  const { t } = useTranslation();
  const { data: blogs, loading: blogsLoading, error: blogsError } = useAPI('blogs');
  const { data: poems, loading: poemsLoading, error: poemsError } = useAPI('poems');
  const { data: galleryItems, loading: galleryLoading, error: galleryError } = useAPI('gallery');
  
  const latestBlogs = blogs.slice(0, 3);
  const latestPoems = poems.slice(0, 3);
  
  const imageOnlyItems = galleryItems.filter(item => item.mediaType === 'image');
  const featuredGalleryItems = imageOnlyItems.slice(0, 8);



  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const baseServerUrl = APP_CONFIG.getBaseServerUrl();

  const getGalleryImageUrl = (item) => {
    if (!item || !item.mediaUrl) return null;
    return item.mediaUrl.startsWith('http') ? item.mediaUrl : `${baseServerUrl}${item.mediaUrl}`;
  };

  const truncatePoetryContent = (content, maxLines = 5) => {
    if (!content) return '';
    
    const lines = content.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length <= maxLines) {
      return content;
    }
    
    const truncatedLines = lines.slice(0, maxLines);
    const result = truncatedLines.join('\n');
    return result;
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${baseServerUrl}${image}`;
  };

  return (
    <div className="min-h-screen bg-black">
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-white/10 rounded-full animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-pulse delay-2000"></div>
          <div className="absolute bottom-32 right-1/3 w-24 h-24 bg-white/10 rounded-full animate-pulse delay-500"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl mb-6 fade-in-up script-font text-white" style={{ fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
              {t('home.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto fade-in-up">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up">
              <Link
                to="/poetry"
                className="bg-gray-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-700 transition-colors border border-white"
              >
                {t('home.explorePoetry')}
              </Link>
              <Link
                to="/gallery"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-black transition-colors"
              >
                {t('home.viewGallery')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('home.visualStories')}</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('home.visualStoriesSubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <div key={`loading-${index}`} className="aspect-w-1 aspect-h-1 rounded-lg border-2 border-gray-400 overflow-hidden" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                  <div className="w-full h-48 bg-gray-900 animate-pulse flex items-center justify-center">
                    <div className="text-gray-500 text-sm">{t('home.loadingGallery')}</div>
                  </div>
                </div>
              ))
            ) : galleryError ? (
              Array.from({ length: 8 }).map((_, index) => (
                <div key={`error-${index}`} className="aspect-w-1 aspect-h-1 rounded-lg border-2 border-gray-400 overflow-hidden" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                    <div className="w-full h-48 bg-gray-900 flex items-center justify-center">
                      <div className="text-gray-500 text-sm">{t('home.galleryItem')} {index + 1}</div>
                    </div>
                </div>
              ))
            ) : featuredGalleryItems.length > 0 ? (
              featuredGalleryItems.map((item, index) => {
                const imageUrl = getGalleryImageUrl(item);
                return (
                  <div key={item._id || item.id || index} className="aspect-w-1 aspect-h-1 rounded-lg border-2 border-gray-400 overflow-hidden" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={item.title || `${t('home.galleryItem')} ${index + 1}`} 
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-full h-48 bg-gray-900 flex items-center justify-center" 
                      style={{ display: imageUrl ? 'none' : 'flex' }}
                    >
                      <div className="text-center text-gray-400">
                        <svg className="w-8 h-8 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        <p className="text-xs font-medium">{item.title || `${t('home.galleryItem')} ${index + 1}`}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              Array.from({ length: 8 }).map((_, index) => (
                <div key={`placeholder-${index}`} className="aspect-w-1 aspect-h-1">
                  <div className="w-full h-48 bg-gray-900 rounded-lg flex items-center justify-center">
                    <div className="text-gray-500 text-sm">Gallery item {index + 1}</div>
                  </div>
              </div>
              ))
            )}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/gallery"
              className="bg-gray-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-700 transition-colors border border-white"
            >
              {t('home.viewFullGallery')}
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('home.featuredPoetry')}</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('home.featuredPoetrySubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {poemsLoading ? (
              <div className="col-span-full text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-gray-300">{t('home.loadingPoetry')}</p>
              </div>
            ) : poemsError ? (
              <div className="col-span-full text-center py-8">
                <p className="text-red-400">{t('home.errorLoadingPoetry')}</p>
              </div>
            ) : latestPoems.length > 0 ? (
              latestPoems.map((poem, index) => {
                const lines = poem.content.split('\n').filter(line => line.trim() !== '');
                const displayText = lines.slice(0, 4).join('\n'); // Always show first 4 lines
                
                return (
                  <div key={poem.id || poem._id || index} className="bg-gray-800 p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow border-2 border-gray-400" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                    <h3 className="text-xl font-semibold text-white mb-4">{poem.title}</h3>
                    <div className="poetry-text text-gray-300 mb-4 text-base leading-relaxed" style={{ height: '120px', overflow: 'hidden' }}>
                      {displayText.split('\n').map((line, i) => (
                        <div key={i} className="mb-1">
                      {line}
                        </div>
                  ))}
                    </div>
                    {lines.length > 0 && (
                      <div className="mb-4 text-left">
                <Link
                  to="/poetry"
                          className="text-white hover:text-gray-300 text-sm transition-colors"
                >
                          {t('home.seeMore')}
                </Link>
                      </div>
                    )}
                    <p className="text-sm text-gray-400 mb-3">- {poem.author}</p>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-300">{t('poetry.noPoetry')}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('home.latestBlogs')}</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('home.latestBlogsSubtitle')}
            </p>
          </div>
          
          {blogsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <span className="ml-4 text-gray-300">{t('home.loadingBlogs')}</span>
            </div>
          ) : blogsError ? (
            <div className="text-center py-12">
              <div className="text-red-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t('home.unableToLoadBlogs')}</h3>
              <p className="text-gray-300">{t('home.errorLoadingBlogs')}</p>
            </div>
          ) : latestBlogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestBlogs.map((blog) => (
                <div key={blog._id || blog.id} className="bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow border-2 border-gray-400 max-w-sm mx-auto" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                  {blog.image ? (
                    <img 
                      src={blog.image.startsWith('http') ? blog.image : `${baseServerUrl}${blog.image}`} 
                      alt={blog.title} 
                      className="w-full h-40 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-40 flex items-center justify-center bg-gray-900 ${blog.image ? 'hidden' : 'flex'}`}
                  >
                    <div className="text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <p className="text-sm font-medium">{t('home.blogPost')}</p>
                    </div>
                  </div>
                <div className="p-4">
                    <div className="text-xs text-gray-400 mb-2">{formatDate(blog.createdAt)}</div>
                    <h3 className="text-base font-semibold text-white mb-2">{blog.title}</h3>
                    <p className="text-xs text-gray-300 mb-3">{truncateText(blog.excerpt, 100)}</p>
                  <Link
                    to="/blogs"
                    className="text-sm text-white hover:text-gray-300 font-medium"
                  >
                    {t('home.seeMore')}
                  </Link>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <p className="text-lg text-white">{t('home.noBlogsAvailable')}</p>
                <p className="text-sm text-gray-300">{t('home.checkBackSoon')}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SHOP DISABLED - Creative Products Section - Client request */}
      {/* <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('home.creativeProducts')}</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('home.productsSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productsLoading ? (
              <div className="col-span-full text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p className="text-gray-300">{t('home.loadingProducts')}</p>
              </div>
            ) : productsError ? (
              <div className="col-span-full text-center py-8">
                <p className="text-red-400">{t('home.errorLoadingProducts')}</p>
              </div>
            ) : featuredProducts.length > 0 ? (
              featuredProducts.map((product, index) => {
                const truncatedDescription = truncateProductDescription(product.description);
                
                return (
                  <div key={product.id || product._id || index} className="bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group border-2 border-gray-400" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                    <div className="aspect-w-16 aspect-h-12 relative">
                      {product.image ? (
                        <img
                          src={getImageUrl(product.image)}
                          alt={product.name}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-48 bg-gray-900 flex items-center justify-center" style={{ display: product.image ? 'none' : 'flex' }}>
                        <div className="text-center text-gray-400">
                          <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                          <p className="text-sm font-medium">{t('home.product')}</p>
                        </div>
                      </div>
                    </div>
                    
                <div className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-2">{product.name}</h3>
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed" style={{ height: '2.5rem', overflow: 'hidden' }}>
                        {truncatedDescription}
                      </p>
                      
                  <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-white">${product.price?.toFixed(2)}</span>
                        <button 
                          onClick={() => window.location.href = '/shop'}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            product.inStock 
                              ? 'bg-gray-700 text-white hover:bg-gray-600 border border-white' 
                              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          }`}
                          disabled={!product.inStock}
                        >
                          {product.inStock ? t('home.shop') : t('home.outOfStock')}
                    </button>
                  </div>
                </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-300">{t('home.noProductsAvailable')}</p>
              </div>
            )}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center px-8 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium border border-white"
            >
              {t('home.visitShop')}
            </Link>
          </div>
        </div>
      </section> */}

      <section className="py-16 bg-black text-white border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">{t('home.joinCommunity')}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-300">
            {t('home.joinCommunitySubtitle')}
          </p>
          <Link
            to="/contact"
            className="bg-gray-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-700 transition-colors border border-white"
          >
            {t('contact.title')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
